import { useStyles, Button, MessageTooltip } from '@archipro-design/aria';
import { PushPinFill, PushPinLine } from '@archipro-design/icons';
import * as S from './DetailPageButtons.style';
import * as React from 'react';

interface PinToDirectoryButtonProps {
    isPinned: boolean;
    /** Pinning is only available while the project is in the directory. */
    isInDirectory: boolean;
    onToggle: (pinned: boolean) => void;
}

export const PinToDirectoryButton = ({
    isPinned,
    isInDirectory,
    onToggle,
}: PinToDirectoryButtonProps) => {
    const { css } = useStyles();
    const [tooltipOpen, setTooltipOpen] = React.useState(false);

    // Profile-only projects never show as pinned: leaving the directory
    // auto-unpins on the server, so any lingering pinned flag is stale.
    const showAsPinned = isPinned && isInDirectory;

    const tooltipLabel = !isInDirectory
        ? 'Add to directory to enable pinning'
        : showAsPinned
          ? 'Unpin from top of directory'
          : 'Pin to top of directory';

    return (
        <MessageTooltip
            open={tooltipOpen}
            trigger={
                <div
                    onMouseEnter={() => setTooltipOpen(true)}
                    onMouseLeave={() => setTooltipOpen(false)}
                >
                    <Button
                        className={css(S.ActionButton)}
                        onClick={() => onToggle(!showAsPinned)}
                        disabled={!isInDirectory}
                        // The tooltip's trigger validation throws on a disabled
                        // descendant without this; the wrapper keeps the hover.
                        styles={
                            !isInDirectory
                                ? { pointerEvents: 'none' }
                                : undefined
                        }
                        icon={showAsPinned ? <PushPinFill /> : <PushPinLine />}
                        size={30}
                        color="white"
                        variables={{
                            iconPadding: 0,
                            buttonPadding: 16,
                            buttonRadius: `50%`,
                        }}
                    />
                </div>
            }
            content={{
                children: tooltipLabel,
                className: css(S.TooltipContent),
                size: 18,
                padding: 12,
                color: 'white',
            }}
            position="after"
            offset={[1, 9]}
        />
    );
};
